import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { loadStreak } from '../state';
import { Wordmark } from './ui';
import { AuthModal } from './AuthModal';
import { AccountModal } from './AccountModal';
import { HelpMenuModal, HowToPlayModal } from './modals';
import { Tutorial } from './Tutorial';

// Canonical app header used by every top-level route. Owns its own
// modal stack (account / sign-in / how-to-play / tutorial) so each
// route just drops `<SharedHeader activeView="..." />` in and gets the
// same behavior.
//
// `activeView` highlights the matching nav link with bolder weight. On
// non-play surfaces the "Today" link is a hard navigation back to `/`;
// on the play surface it triggers an in-app handler so today's puzzle
// resets without a full page reload. Same idea for "Archive" — owned
// by App.tsx where it makes sense; elsewhere a hard nav to `/?archive=1`
// so the main app can open the modal post-mount.

export type ActiveView = 'today' | 'design' | 'browse' | 'profile' | 'play' | 'admin' | 'other';

interface SharedHeaderProps {
  activeView: ActiveView;
  /// Pass when the parent (App.tsx) wants to own the "Today" / "Archive"
  /// behavior in-place. Optional everywhere else — defaults to hard
  /// navigation back to `/` (with a query param for Archive).
  onJumpToToday?: () => void;
  onOpenArchive?: () => void;
  /// When set, the header renders an extra navigation node (typically a
  /// "← Browse" back link on the play surface). Each entry is rendered
  /// as a link or button depending on `href`.
  trailingNav?: { label: string; href?: string; onClick?: () => void }[];
  /// Anonymous-user streak counter from the parent. When provided this
  /// supersedes the localStorage fallback so cross-component updates
  /// (App.tsx bumps streakInfo on mount, header rerenders) stay in
  /// sync without a storage-event dance.
  anonStreakOverride?: number;
}

export function SharedHeader({
  activeView,
  onJumpToToday,
  onOpenArchive,
  trailingNav,
  anonStreakOverride,
}: SharedHeaderProps) {
  const auth = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  // Anonymous-streak fallback for routes that don't have an App-owned
  // streakInfo to pipe in (designer / browse / profile). On those
  // surfaces we just peek at localStorage on mount — they're navigated
  // to via hard reloads so the snapshot is fresh enough.
  const [anonStreakLocal] = useState(() => loadStreak().streak);
  const anonStreak = anonStreakOverride ?? anonStreakLocal;
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showHelpText, setShowHelpText] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showAuth, setShowAuth] = useState<false | 'login' | 'signup'>(false);
  const [showAccount, setShowAccount] = useState(false);

  const isActive = (v: ActiveView): React.CSSProperties =>
    activeView === v ? { fontWeight: 600, color: 'var(--ink)' } : {};

  function handleArchive() {
    if (onOpenArchive) { onOpenArchive(); return; }
    // Non-app pages: bounce to the main app with a hint to open the
    // archive immediately after mount. App.tsx picks up `?archive=1`.
    window.location.href = '/?archive=1';
  }
  function handleToday() {
    if (onJumpToToday) { onJumpToToday(); return; }
    window.location.href = '/';
  }

  return (
    <>
      <header className="hm-header">
        <div className="hm-header-left">
          <a href="/" style={{ textDecoration: 'none', color: 'inherit' }} aria-label="Hexponents home">
            <Wordmark />
          </a>
          <nav className="hm-nav" aria-label="Top navigation">
            <button onClick={handleToday} style={isActive('today')}>Today</button>
            <button onClick={() => setShowHelpMenu(true)}>How to play</button>
            <button
              onClick={handleArchive}
              title={auth.user?.isPremium ? 'Past puzzles' : 'Past puzzles are a Premium feature'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              Archive
              {!auth.user?.isPremium && <span style={{ fontSize: 10, opacity: 0.7 }}>★</span>}
            </button>
            <a
              href="/designer"
              title={auth.user?.isPremium ? 'Design + share your own puzzle' : 'Authoring is a Premium feature'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none', color: 'inherit', ...isActive('design') }}
            >
              Design
              {!auth.user?.isPremium && <span style={{ fontSize: 10, opacity: 0.7 }}>★</span>}
            </a>
            <a
              href="/browse"
              title="Browse community-authored puzzles"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none', color: 'inherit', ...isActive('browse') }}
            >
              Browse
            </a>
            {trailingNav?.map((item, i) => (
              item.href
                ? <a key={i} href={item.href} style={{ textDecoration: 'none', color: 'inherit' }}>{item.label}</a>
                : <button key={i} onClick={item.onClick}>{item.label}</button>
            ))}
          </nav>
        </div>
        <div className="hm-header-right">
          <span
            className="hm-streak"
            title={auth.user ? `Play streak (longest ${auth.stats?.longestStreak ?? 0})` : 'Play streak — sign in to keep across devices'}
          >
            <svg
              className="hm-streak-flame"
              width="14"
              height="14"
              viewBox="0 0 16 16"
              aria-hidden="true"
            >
              <path
                d="M8 1.5c.4 1.7 1.7 2.6 2.7 3.6 1 1 1.8 2.1 1.8 3.7a4.5 4.5 0 0 1-9 0c0-1.2.5-2 1.2-2.4 0 .8.5 1.4 1.1 1.4.7 0 1.2-.6 1.2-1.6 0-1.8-.4-3.4 1-4.7z"
                fill="currentColor"
              />
            </svg>
            <span>Streak {auth.user ? (auth.stats?.streak ?? 0) : anonStreak}</span>
          </span>
          {auth.loading ? null : auth.user ? (
            <>
              {!auth.user.isPremium && (
                <a
                  href="/?upgrade=1"
                  className="hm-action"
                  title="Upgrade to Hexponents Premium"
                  style={{ textDecoration: 'none' }}
                >Upgrade</a>
              )}
              <button
                type="button"
                className="hm-action"
                title={`Account · ${auth.user.email}`}
                onClick={() => setShowAccount(true)}
              >
                {auth.user.displayName}
                {auth.user.isPremium && (
                  <span style={{ marginLeft: 6, fontSize: 9, opacity: 0.65 }}>★</span>
                )}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="hm-action"
              onClick={() => setShowAuth('login')}
            >Sign in</button>
          )}
          <button
            type="button"
            className="hm-icon-btn hm-icon-btn-mobile-only"
            onClick={() => setShowHelpMenu(true)}
            aria-label="How to play"
            title="How to play"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M6.5 6.2a1.5 1.5 0 0 1 3 .1c0 .9-1.5 1.1-1.5 2.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="8" cy="11.4" r="0.7" fill="currentColor"/>
            </svg>
          </button>
          <button
            type="button"
            className="hm-icon-btn"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          >
            {theme === 'dark' ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5"/>
                <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M8 1.5v1.5"/><path d="M8 13v1.5"/>
                  <path d="M1.5 8h1.5"/><path d="M13 8h1.5"/>
                  <path d="M3.4 3.4l1.05 1.05"/><path d="M11.55 11.55l1.05 1.05"/>
                  <path d="M3.4 12.6l1.05-1.05"/><path d="M11.55 4.45l1.05-1.05"/>
                </g>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M13 9.5A6 6 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </div>
      </header>

      {showTutorial && <Tutorial onClose={() => setShowTutorial(false)} />}
      {showHelpMenu && (
        <HelpMenuModal
          onReplayTutorial={() => { setShowHelpMenu(false); setShowTutorial(true); }}
          onShowText={() => { setShowHelpMenu(false); setShowHelpText(true); }}
          onClose={() => setShowHelpMenu(false)}
        />
      )}
      {showHelpText && <HowToPlayModal onClose={() => setShowHelpText(false)} />}
      {showAuth && (
        <AuthModal
          initialMode={showAuth}
          onClose={() => setShowAuth(false)}
        />
      )}
      {showAccount && (
        <AccountModal
          onClose={() => setShowAccount(false)}
          onUpgradeRequested={() => {
            // Upgrade flow lives on the main app (carries the Stripe
            // checkout machinery). Non-app routes hand off via redirect.
            setShowAccount(false);
            window.location.href = '/?upgrade=1';
          }}
          onBuyHints={() => {
            setShowAccount(false);
            window.location.href = '/?hints=1';
          }}
        />
      )}
    </>
  );
}
